import { Badge } from "@d12labs/ui/badge";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { List, ListHeader, ListRow } from "@d12labs/ui/data-list";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Pager } from "@d12labs/ui/pager";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@d12labs/ui/select";
import { SkeletonRow } from "@d12labs/ui/skeleton";
import { Toolbar } from "@d12labs/ui/toolbar";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ScrollText, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { fitstreakApi } from "../api";
import {
	ACTIVITY_TYPE_ICONS,
	ACTIVITY_TYPE_LABELS,
	formatDistance,
	formatDuration,
	formatRelativeDay,
} from "../format";
import type { Activity, ActivityType } from "../types";

const ALL_TYPES: ActivityType[] = ["run", "ride", "swim", "strength", "mobility", "hike", "other"];

// Browser-local today is fine for relative ("Yesterday") labels in the list.
function clientToday(): string {
	return new Date().toLocaleDateString("en-CA"); // en-CA renders YYYY-MM-DD
}

const ACTIVITY_COLS = [
	{ label: "Activity", width: "1fr" },
	{ label: "Date", width: "100px" },
	{ label: "Source", width: "100px" },
	{ label: "Duration", width: "80px", align: "right" as const },
	{ label: "Distance", width: "80px", align: "right" as const },
	{ label: "", width: "32px" },
];

function activityTitle(a: Activity): string {
	return (
		a.title ??
		(a.details?.kind === "strength"
			? `${a.details.protocol.toUpperCase()} strength`
			: ACTIVITY_TYPE_LABELS[a.type])
	);
}

function ActivitySourceBadge({ source }: { source: Activity["source"] }) {
	if (source === "garmin") return <Badge tone="primary">Garmin</Badge>;
	if (source === "trainingpeaks") return <Badge tone="subtle">TrainingPeaks</Badge>;
	return <Badge tone="neutral">manual</Badge>;
}

export function ActivityLogPage() {
	const qc = useQueryClient();
	const [type, setType] = useState<ActivityType | "all">("all");
	const [page, setPage] = useState(1);

	const query = useQuery({
		queryKey: ["fitstreak", "activities", { type, page }],
		queryFn: () =>
			fitstreakApi.listActivities({
				query: { page, pageSize: 20, ...(type !== "all" ? { type } : {}) },
			}),
	});

	const remove = useMutation({
		mutationFn: (id: string) => fitstreakApi.removeActivity({ params: { id } }),
		onSuccess: () => {
			toast.success("Activity deleted");
			qc.invalidateQueries({ queryKey: ["fitstreak"] });
		},
		meta: { errorMessage: "Delete failed" },
	});

	const today = clientToday();
	const data = query.data;

	return (
		<>
			<PageHeader
				icon={ScrollText}
				title="Activity log"
				subtitle="Your full training history."
				badges={
					data ? (
						<Badge tone="neutral">
							{data.total} {data.total === 1 ? "activity" : "activities"}
						</Badge>
					) : undefined
				}
			/>
			<Toolbar
				left={
					<Select
						value={type}
						onValueChange={(v) => {
							setType(v as ActivityType | "all");
							setPage(1);
						}}
					>
						<SelectTrigger size="sm" className="w-40">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All types</SelectItem>
							{ALL_TYPES.map((t) => (
								<SelectItem key={t} value={t}>
									{ACTIVITY_TYPE_LABELS[t]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				}
				right={
					data ? (
						<span className="text-meta text-ink-subtle">
							Page {data.page}/{data.pageCount}
						</span>
					) : undefined
				}
			/>
			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-3xl space-y-4">
					{query.isLoading ? (
						<List>
							<ListHeader columns={ACTIVITY_COLS} />
							{[1, 2, 3, 4, 5].map((i) => (
								<SkeletonRow key={i} columns={ACTIVITY_COLS} />
							))}
						</List>
					) : !data || data.items.length === 0 ? (
						<EmptyState
							title={
								type !== "all" ? `No ${ACTIVITY_TYPE_LABELS[type]} activities` : "No activities yet"
							}
							body={
								type !== "all"
									? "Try a different filter, or log your first session of this type."
									: "Log your first session to start building your history."
							}
						/>
					) : (
						<>
							<List>
								<ListHeader columns={ACTIVITY_COLS} />
								{data.items.map((a) => {
									const Icon = ACTIVITY_TYPE_ICONS[a.type];
									const relDay = formatRelativeDay(a.localDate, today);
									const dur = formatDuration(a.durationSec);
									const dist = formatDistance(a.distanceM);
									return (
										<ListRow
											key={a.id}
											interactive={false}
											cells={[
												{
													value: (
														<span className="flex items-center gap-2">
															<Icon className="size-3.5 flex-none text-ink-muted" />
															{activityTitle(a)}
														</span>
													),
													width: "1fr",
												},
												{ value: relDay, width: "100px", muted: true },
												{
													value: <ActivitySourceBadge source={a.source} />,
													width: "100px",
												},
												{
													value: dur ?? "—",
													width: "80px",
													align: "right",
													muted: true,
												},
												{
													value: dist ?? "—",
													width: "80px",
													align: "right",
													muted: true,
												},
												{
													value: (
														<ConfirmDialog
															title="Delete activity?"
															description="This removes the activity and recomputes your streak. This cannot be undone."
															confirmLabel="Delete"
															destructive
															onConfirm={() => remove.mutate(a.id)}
														>
															<button
																type="button"
																aria-label="Delete activity"
																className="flex size-6 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-destructive/10 hover:text-destructive"
															>
																<Trash2 className="size-3" />
															</button>
														</ConfirmDialog>
													),
													width: "32px",
												},
											]}
										/>
									);
								})}
							</List>
							<Pager
								page={page}
								pageCount={data.pageCount}
								onPageChange={setPage}
								variant="outline"
								label={
									<span className="text-meta text-ink-subtle">
										{data.total} activit{data.total === 1 ? "y" : "ies"} · page {data.page}/
										{data.pageCount}
									</span>
								}
							/>
						</>
					)}
				</div>
			</PageBody>
		</>
	);
}
